<script setup>
/* global axios */
// Icerik > Medya Kutuphanesi: gonderilerin ortak dosya deposu.
//
// NEDEN ayri bir kutuphane: ayni gorsel genellikle birden fazla gonderide
// kullaniliyor (kampanya gorseli, urun fotografi). Her gonderiye ayri
// yukleme, ayni dosyanin onlarca kopyasini ve "hangisi guncel" sorusunu
// uretir.
//
// Silme, dosyayi kullanan gonderi varsa sunucuda REDDEDILIYOR ve hangi
// gonderiler oldugu geri geliyor. O metni oldugu gibi gosteriyoruz:
// kullanicinin karari icin gereken bilgi zaten orada.
//
// Ham 'axios' PAKETINI ice aktarmiyoruz. Chatwoot kimlik basliklarini
// window.axios uzerinde tutuyor; paketten gelen ornek 401 doner.
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { hataMetniniCoz, tarihMetni } from './icerik-ortak';

const { t, locale } = useI18n();
const route = useRoute();

const uc = computed(
  () => `/api/v1/accounts/${route.params.accountId}/icerik/medya`
);

// Sunucunun kabul ettigi turler (OmnupMedyaController). Dosya secicide de
// ayni listeyi veriyoruz: kullanici kabul edilmeyecek bir dosyayi hic
// secemesin, hatayi yukleme sonrasi ogrenmesin.
const KABUL = 'image/jpeg,image/png,image/webp,video/mp4';

const TUR_SECENEKLERI = [
  { deger: '', anahtar: 'ICERIK.MEDYA_TUR_HEPSI' },
  { deger: 'gorsel', anahtar: 'ICERIK.MEDYA_TUR_GORSEL' },
  { deger: 'video', anahtar: 'ICERIK.MEDYA_TUR_VIDEO' },
];

const medyalar = ref([]);
const yukleniyor = ref(true);
const hata = ref('');

const turSuzgeci = ref('');
const arama = ref('');

const yukleniyorDosya = ref(false);
const yuklenenSayisi = ref(0);
const yuklenecekSayisi = ref(0);
const silinen = ref(null);

// Silme reddedildiginde sunucunun donen metni burada durur. Gecici bir uyari
// (alert) yeterli degil: kullanici hangi gonderileri temizlemesi gerektigini
// okurken mesaj kaybolmamali.
const silmeUyarisi = ref('');

const dosyaGirisi = ref(null);

const suzgecVar = computed(
  () => turSuzgeci.value !== '' || arama.value.trim() !== ''
);

// Suzgec hizli degistiginde gec donen eski yanit yeni listenin uzerine
// yazmasin diye her istege sira numarasi veriyoruz.
let istekSayaci = 0;

const yukle = async () => {
  const sira = (istekSayaci += 1);
  yukleniyor.value = true;
  hata.value = '';

  const params = {};
  if (turSuzgeci.value) params.tur = turSuzgeci.value;
  if (arama.value.trim()) params.arama = arama.value.trim();

  try {
    const { data } = await axios.get(uc.value, { params });
    if (sira !== istekSayaci) return;
    medyalar.value = data.medya || [];
  } catch (e) {
    if (sira !== istekSayaci) return;
    hata.value = hataMetniniCoz(e, t('ICERIK.HATA_BILINMEYEN'));
    medyalar.value = [];
  } finally {
    if (sira === istekSayaci) yukleniyor.value = false;
  }
};

onMounted(yukle);

// Aramada her tusa basista istek atmak sunucuyu gereksiz mesgul eder;
// kullanici yazmayi birakinca ariyoruz.
let aramaZamani = null;
watch(arama, () => {
  if (aramaZamani) window.clearTimeout(aramaZamani);
  aramaZamani = window.setTimeout(yukle, 350);
});

watch(turSuzgeci, yukle);

const suzgeciTemizle = () => {
  turSuzgeci.value = '';
  arama.value = '';
};

// --- yukleme ---------------------------------------------------------------

const dosyaSec = () => dosyaGirisi.value?.click();

// Dosyalari TEK TEK gonderiyoruz.
//
// NEDEN hepsini bir istekte degil: 50 MB'lik bir video ile 200 KB'lik bir
// gorseli ayni govdeye koymak, birinin reddi yuzunden digerini de kaybetmek
// demek. Tek tek gidince basarili olanlar kutuphaneye girer, sorunlu dosya
// adiyla birlikte bildirilir.
const dosyalariYukle = async olay => {
  const dosyalar = Array.from(olay.target.files || []);
  if (!dosyalar.length) return;

  yukleniyorDosya.value = true;
  yuklenenSayisi.value = 0;
  yuklenecekSayisi.value = dosyalar.length;
  silmeUyarisi.value = '';

  const hatalar = [];

  // eslint-disable-next-line no-restricted-syntax
  for (const dosya of dosyalar) {
    const govde = new FormData();
    govde.append('dosya', dosya);
    try {
      // eslint-disable-next-line no-await-in-loop
      await axios.post(uc.value, govde, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      yuklenenSayisi.value += 1;
    } catch (e) {
      hatalar.push(`${dosya.name}: ${hataMetniniCoz(e, t('ICERIK.HATA_BILINMEYEN'))}`);
    }
  }

  yukleniyorDosya.value = false;
  // Ayni dosyayi yeniden secebilmek icin girisi sifirliyoruz; tarayici ayni
  // degeri ikinci kez "degisiklik" saymiyor.
  if (dosyaGirisi.value) dosyaGirisi.value.value = '';

  if (hatalar.length) {
    // Hangi dosyanin neden reddedildigini yaziyoruz; "yukleme basarisiz"
    // demek kullaniciyi denemeye devam etmekten baska bir yere goturmez.
    useAlert(hatalar.join(' | '));
  } else {
    useAlert(t('ICERIK.MEDYA_YUKLENDI', { sayi: yuklenenSayisi.value }));
  }

  await yukle();
};

// --- silme -----------------------------------------------------------------

const sil = async medya => {
  if (!window.confirm(t('ICERIK.MEDYA_SIL_ONAY'))) return;

  silinen.value = medya.id;
  silmeUyarisi.value = '';
  try {
    await axios.delete(`${uc.value}/${medya.id}`);
    useAlert(t('ICERIK.MEDYA_SILINDI'));
    await yukle();
  } catch (e) {
    // Kullanan gonderi varsa sunucu hangileri oldugunu yaziyor; metni oldugu
    // gibi tutuyoruz.
    silmeUyarisi.value = hataMetniniCoz(e, t('ICERIK.HATA_BILINMEYEN'));
  } finally {
    silinen.value = null;
  }
};

// --- gorunum yardimcilari --------------------------------------------------

// "52428800 bayt" kullaniciya hicbir sey anlatmaz.
const boyutMetni = bayt => {
  const sayi = Number(bayt || 0);
  if (!sayi) return '';
  if (sayi < 1024 * 1024) return `${Math.round(sayi / 1024)} KB`;
  const mb = sayi / (1024 * 1024);
  return `${mb % 1 === 0 ? mb : mb.toFixed(1)} MB`;
};

const olcuMetni = medya =>
  medya.genislik && medya.yukseklik
    ? `${medya.genislik} x ${medya.yukseklik}`
    : '';

const tarih = deger => tarihMetni(deger, locale.value);
</script>

<template>
  <div class="flex flex-col gap-4 p-6">
    <div class="flex flex-wrap items-end gap-3">
      <label class="flex flex-col gap-1">
        <span class="text-xs text-n-slate-11">{{ t('ICERIK.MEDYA_TUR') }}</span>
        <select
          v-model="turSuzgeci"
          class="h-8 px-2 text-sm border rounded-md border-n-weak bg-n-alpha-1 text-n-slate-12"
        >
          <option
            v-for="secenek in TUR_SECENEKLERI"
            :key="secenek.deger"
            :value="secenek.deger"
          >
            {{ t(secenek.anahtar) }}
          </option>
        </select>
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-xs text-n-slate-11">{{ t('ICERIK.MEDYA_ARAMA') }}</span>
        <input
          v-model="arama"
          type="search"
          class="h-8 px-2 text-sm border rounded-md w-60 border-n-weak bg-n-alpha-1 text-n-slate-12"
          :placeholder="t('ICERIK.MEDYA_ARAMA_YER')"
        />
      </label>

      <Button
        v-if="suzgecVar"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-lucide-x"
        :label="t('ICERIK.SUZGEC_TEMIZLE')"
        @click="suzgeciTemizle"
      />

      <span class="grow" />

      <span class="text-xs text-n-slate-11">
        {{ t('ICERIK.MEDYA_SAYI', { sayi: medyalar.length }) }}
      </span>
      <Button
        size="sm"
        variant="faded"
        icon="i-lucide-refresh-cw"
        :label="t('ICERIK.YENILE')"
        :disabled="yukleniyor"
        @click="yukle"
      />
      <Button
        size="sm"
        variant="solid"
        icon="i-lucide-upload"
        :label="t('ICERIK.MEDYA_YUKLE')"
        :is-loading="yukleniyorDosya"
        :disabled="yukleniyorDosya"
        @click="dosyaSec"
      />
      <!-- Gorunmez dosya girisi: tarayicinin kendi dugmesi tasarim
           tokenlarimiza uymuyor, ama dosya secici yalnizca gercek bir
           <input type="file"> ile acilabiliyor. -->
      <input
        ref="dosyaGirisi"
        type="file"
        multiple
        class="hidden"
        :accept="KABUL"
        @change="dosyalariYukle"
      />
    </div>

    <p class="text-xs text-n-slate-11">{{ t('ICERIK.MEDYA_IPUCU') }}</p>

    <p v-if="yukleniyorDosya" class="text-xs text-n-blue-11">
      {{
        t('ICERIK.MEDYA_YUKLENIYOR', {
          yapilan: yuklenenSayisi,
          toplam: yuklenecekSayisi,
        })
      }}
    </p>

    <!-- Silme reddi: hangi gonderilerin kullandigini yazan metin ekranda
         KALIR; gecici bir uyari olarak gostermek kullaniciyi listeyi
         ezberlemeye zorlardi. -->
    <div
      v-if="silmeUyarisi"
      class="flex items-start gap-2 px-3 py-2 rounded-md bg-n-alpha-1"
    >
      <span class="i-lucide-triangle-alert size-4 shrink-0 text-n-ruby-11" />
      <p class="text-xs text-n-ruby-11">{{ silmeUyarisi }}</p>
      <span class="grow" />
      <button
        type="button"
        class="text-xs shrink-0 text-n-slate-11 hover:text-n-slate-12"
        @click="silmeUyarisi = ''"
      >
        {{ t('ICERIK.KAPAT') }}
      </button>
    </div>

    <div v-if="yukleniyor" class="flex justify-center p-10">
      <Spinner class="text-n-brand" />
    </div>

    <div v-else-if="hata" class="flex flex-col items-start gap-3">
      <p class="text-sm text-n-ruby-11">{{ hata }}</p>
      <Button
        size="sm"
        variant="faded"
        icon="i-lucide-refresh-cw"
        :label="t('ICERIK.YENIDEN_DENE')"
        @click="yukle"
      />
    </div>

    <!-- Iki ayri bos durum: hic dosya olmamasi ile suzgece uyan dosya
         olmamasi farkli sorunlar. -->
    <div
      v-else-if="!medyalar.length && suzgecVar"
      class="max-w-xl p-10 mx-auto text-center border rounded-lg border-n-weak"
    >
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('ICERIK.MEDYA_SUZGEC_BOS_BASLIK') }}
      </p>
      <p class="mt-1 mb-4 text-xs text-n-slate-11">
        {{ t('ICERIK.MEDYA_SUZGEC_BOS_ACIKLAMA') }}
      </p>
      <Button
        size="sm"
        variant="faded"
        icon="i-lucide-x"
        :label="t('ICERIK.SUZGEC_TEMIZLE')"
        @click="suzgeciTemizle"
      />
    </div>

    <div
      v-else-if="!medyalar.length"
      class="max-w-xl p-10 mx-auto text-center border rounded-lg border-n-weak"
    >
      <span
        class="inline-grid mb-3 rounded-full size-10 place-content-center bg-n-alpha-1 text-n-slate-11"
      >
        <span class="i-lucide-image size-5" />
      </span>
      <p class="text-sm font-medium text-n-slate-12">
        {{ t('ICERIK.MEDYA_BOS_BASLIK') }}
      </p>
      <p class="mt-1 mb-4 text-xs text-n-slate-11">
        {{ t('ICERIK.MEDYA_BOS_ACIKLAMA') }}
      </p>
      <Button
        size="sm"
        variant="solid"
        icon="i-lucide-upload"
        :label="t('ICERIK.MEDYA_YUKLE')"
        :disabled="yukleniyorDosya"
        @click="dosyaSec"
      />
    </div>

    <ul
      v-else
      class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
    >
      <li
        v-for="medya in medyalar"
        :key="medya.id"
        class="flex flex-col overflow-hidden border rounded-lg border-n-weak bg-n-solid-1"
      >
        <div class="relative aspect-square bg-n-alpha-1">
          <img
            v-if="medya.tur === 'gorsel' && medya.url"
            :src="medya.url"
            :alt="medya.ad"
            loading="lazy"
            class="object-cover w-full h-full"
          />
          <!-- Video icin oynatici cizmiyoruz: izgarada onlarca video etiketi
               acmak her birine ayri bir indirme baslatir ve sayfayi
               agirlastirir. Dosya adi, turu ve boyutu tanimaya yetiyor;
               icerigi gormek isteyen "Dosyayi ac" ile yeni sekmede aciyor. -->
          <span
            v-else
            class="grid w-full h-full place-content-center text-n-slate-11"
          >
            <span
              :class="
                medya.tur === 'video' ? 'i-lucide-video' : 'i-lucide-file'
              "
              class="size-8"
            />
          </span>

          <span
            v-if="medya.kullanim_sayisi > 0"
            class="absolute px-2 py-0.5 text-xs rounded-md top-2 right-2 bg-n-solid-1 text-n-slate-11"
            :title="t('ICERIK.MEDYA_KULLANIM_IPUCU')"
          >
            {{ t('ICERIK.MEDYA_KULLANIM', { sayi: medya.kullanim_sayisi }) }}
          </span>
        </div>

        <div class="flex flex-col gap-1 p-3">
          <p class="text-sm truncate text-n-slate-12" :title="medya.ad">
            {{ medya.ad }}
          </p>
          <p class="text-xs text-n-slate-11">
            <span v-if="olcuMetni(medya)">{{ olcuMetni(medya) }} &middot; </span>
            <span v-if="boyutMetni(medya.boyut)">
              {{ boyutMetni(medya.boyut) }} &middot;
            </span>
            {{ tarih(medya.olusturulma) }}
          </p>

          <div class="flex items-center gap-2 mt-1">
            <a
              v-if="medya.url"
              :href="medya.url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-xs underline text-n-blue-11"
            >
              {{ t('ICERIK.MEDYA_AC') }}
            </a>
            <span class="grow" />
            <Button
              size="xs"
              variant="ghost"
              color="ruby"
              icon="i-lucide-trash-2"
              :label="t('ICERIK.SIL')"
              :is-loading="silinen === medya.id"
              :disabled="silinen === medya.id"
              @click="sil(medya)"
            />
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>
